import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import axios from "axios";
import { toast } from "sonner";
import {
  ChevronLeft, Clock, Check, Bell, Calendar, Star,
  Target, TrendingUp, Shield, Loader2, Zap, X,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { DarkModeToggle } from "../components/DarkModeToggle";

const API = process.env.REACT_APP_BACKEND_URL;
const DAY_NAMES = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const TIME_SLOTS = [
  { key: "morning", label: "Morning", desc: "8am – 12pm" },
  { key: "afternoon", label: "Afternoon", desc: "12pm – 5pm" },
  { key: "evening", label: "Evening", desc: "5pm – 8pm" },
];

const fmtTime = (t) => {
  if (!t) return "";
  const [h, m] = t.split(":").map(Number);
  const s = h >= 12 ? "pm" : "am";
  const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return m === 0 ? `${h12}${s}` : `${h12}:${String(m).padStart(2, "0")}${s}`;
};

export default function WaitlistPage() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [offers, setOffers] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  // Form state
  const [days, setDays] = useState([]);
  const [times, setTimes] = useState([]);
  const [duration, setDuration] = useState(120);
  const [testDate, setTestDate] = useState("");
  const [postcode, setPostcode] = useState("");
  const [urgency, setUrgency] = useState("happy_to_wait");
  const [flexibility, setFlexibility] = useState("normal");
  const [packageInterest, setPackageInterest] = useState(false);

  const fetchStatus = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/waitlist/my-status`, { withCredentials: true });
      setStatus(data);
      if (data.on_waitlist) {
        setDays(data.preferred_days || []);
        setTimes(data.preferred_times || []);
        setDuration(data.duration_preference || 120);
        setTestDate(data.test_date || "");
        setPostcode(data.postcode || "");
        setUrgency(data.urgency || "happy_to_wait");
        setFlexibility(data.flexibility || "normal");
        setPackageInterest(data.package_interest || false);
      }
    } catch {}
    setLoading(false);
  }, []);

  const fetchOffers = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/waitlist/my-offers`, { withCredentials: true });
      setOffers(data.offers || []);
    } catch {}
  }, []);

  useEffect(() => { fetchStatus(); fetchOffers(); }, [fetchStatus, fetchOffers]);

  const toggleDay = (d) => setDays(prev => prev.includes(d) ? prev.filter(x => x !== d) : [...prev, d]);
  const toggleTime = (t) => setTimes(prev => prev.includes(t) ? prev.filter(x => x !== t) : [...prev, t]);

  const handleSubmit = async () => {
    if (days.length === 0) { toast.error("Select at least one preferred day"); return; }
    setSubmitting(true);
    try {
      const payload = {
        preferred_days: days, preferred_times: times, duration_preference: duration,
        test_date: testDate, postcode, urgency, flexibility, package_interest: packageInterest,
      };
      if (status?.on_waitlist) {
        await axios.put(`${API}/api/waitlist/update`, payload, { withCredentials: true });
        toast.success("Preferences updated");
      } else {
        await axios.post(`${API}/api/waitlist/join`, payload, { withCredentials: true });
        toast.success("You're on the waitlist!");
      }
      fetchStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
    setSubmitting(false);
  };

  const leaveWaitlist = async () => {
    if (!window.confirm("Leave the waitlist? You'll stop receiving slot offers.")) return;
    try {
      await axios.post(`${API}/api/waitlist/leave`, {}, { withCredentials: true });
      toast.success("Left the waitlist");
      setStatus({ on_waitlist: false });
    } catch (err) { toast.error("Failed"); }
  };

  const acceptOffer = async (offerId) => {
    try {
      const { data } = await axios.post(`${API}/api/waitlist/offers/${offerId}/accept`, {}, { withCredentials: true });
      toast.success("Offer accepted! Redirecting to booking...");
      if (data.redirect) nav(data.redirect);
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const passOffer = async (offerId) => {
    try {
      await axios.post(`${API}/api/waitlist/offers/${offerId}/pass`, {}, { withCredentials: true });
      toast.success("Offer passed");
      fetchOffers();
    } catch {}
  };

  if (loading) return (
    <div className="min-h-dvh no-overflow-x bg-slate-50 dark:bg-[#0c1120] flex items-center justify-center">
      <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
    </div>
  );

  return (
    <div className="min-h-dvh no-overflow-x bg-slate-50 dark:bg-[#0c1120]">
      <header className="bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700 px-4 py-3 sticky top-0 z-40">
        <div className="max-w-lg mx-auto flex items-center gap-3">
          <button data-testid="waitlist-back" onClick={() => nav("/learn")} className="p-2 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800">
            <ChevronLeft className="w-5 h-5 text-slate-500" />
          </button>
          <div className="flex-1">
            <h1 className="text-lg font-extrabold text-slate-900 dark:text-white">Lesson Waitlist</h1>
            <p className="text-xs text-slate-400">Get notified when slots open</p>
          </div>
          <DarkModeToggle />
        </div>
      </header>

      <div className="max-w-lg mx-auto px-4 py-6 space-y-5">

        {/* Offers */}
        {offers.length > 0 && (
          <div data-testid="waitlist-offers" className="space-y-2">
            <h2 className="text-sm font-extrabold text-slate-900 dark:text-white">Available Slots for You</h2>
            {offers.map(offer => (
              <div key={offer.id} data-testid={`offer-${offer.id}`}
                className="bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-800 rounded-2xl p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Zap className="w-4 h-4 text-amber-500" />
                  <p className="text-sm font-bold text-slate-900 dark:text-white">{offer.title}</p>
                </div>
                <p className="text-sm text-slate-600 dark:text-slate-400 mb-3">{offer.message}</p>
                <div className="flex gap-2">
                  <Button data-testid={`accept-${offer.id}`} onClick={() => acceptOffer(offer.id)}
                    className="flex-1 h-10 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-bold rounded-xl">Book Now</Button>
                  <Button data-testid={`pass-${offer.id}`} onClick={() => passOffer(offer.id)}
                    variant="outline" className="h-10 px-4 rounded-xl text-sm">Pass</Button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Welcome / Status */}
        {status?.on_waitlist ? (
          <div data-testid="waitlist-active" className="bg-blue-50 dark:bg-blue-900/15 border border-blue-200 dark:border-blue-800 rounded-2xl p-5">
            <div className="flex items-center gap-2 mb-2">
              <Check className="w-5 h-5 text-blue-500" />
              <p className="text-base font-extrabold text-slate-900 dark:text-white">You're on the waitlist</p>
            </div>
            <p className="text-sm text-slate-500 dark:text-slate-400">We'll notify you when suitable lesson spaces become available.</p>
            <div className="mt-3 flex items-center gap-3">
              <div className="flex-1 bg-blue-200/50 dark:bg-blue-800/30 rounded-full h-2.5 overflow-hidden">
                <div className="bg-blue-500 h-full rounded-full transition-all" style={{ width: `${status.profile_completeness || 0}%` }} />
              </div>
              <span className="text-xs font-bold text-blue-600">{status.profile_completeness || 0}%</span>
            </div>
            <p className="text-[10px] text-blue-400 mt-1">Profile completeness — higher = more offers</p>
          </div>
        ) : (
          <div data-testid="waitlist-welcome" className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 p-5 space-y-3">
            <div className="flex items-center gap-2">
              <Bell className="w-5 h-5 text-blue-500" />
              <h2 className="text-base font-extrabold text-slate-900 dark:text-white">Join the Waitlist</h2>
            </div>
            <p className="text-sm text-slate-500 dark:text-slate-400">No preferred slot available? We'll notify you when suitable spaces open.</p>
          </div>
        )}

        {/* How It Works */}
        <div data-testid="waitlist-explainer" className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 p-5 space-y-3">
          <p className="text-xs font-bold text-slate-400 uppercase tracking-wider">How Priority Works</p>
          <p className="text-sm text-slate-500 dark:text-slate-400">Availability is matched using factors such as:</p>
          <div className="space-y-2">
            {[
              { icon: Calendar, text: "Your preferred days & times" },
              { icon: Target, text: "Practical test date (if booked)" },
              { icon: Star, text: "Lesson package commitment" },
              { icon: TrendingUp, text: "Flexibility of availability" },
              { icon: Clock, text: "How long you've been waiting" },
              { icon: Shield, text: "Readiness for lessons" },
            ].map((item, i) => (
              <div key={i} className="flex items-center gap-2.5">
                <item.icon className="w-4 h-4 text-blue-500 flex-shrink-0" />
                <span className="text-sm text-slate-600 dark:text-slate-400">{item.text}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Preferences Form */}
        <div className="space-y-4">
          <h2 className="text-base font-extrabold text-slate-900 dark:text-white">
            {status?.on_waitlist ? "Update Your Preferences" : "Your Preferences"}
          </h2>

          {/* Days */}
          <div>
            <p className="text-sm font-bold text-slate-900 dark:text-white mb-2">Preferred Days</p>
            <div className="flex flex-wrap gap-2">
              {DAY_NAMES.map((name, i) => (
                <button key={i} data-testid={`day-${i}`} onClick={() => toggleDay(i)}
                  className={`px-3.5 py-2 rounded-xl text-sm font-bold transition-all ${
                    days.includes(i)
                      ? "bg-blue-600 text-white"
                      : "bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-500"
                  }`}>{name.slice(0, 3)}</button>
              ))}
            </div>
          </div>

          {/* Times */}
          <div>
            <p className="text-sm font-bold text-slate-900 dark:text-white mb-2">Preferred Times</p>
            <div className="grid grid-cols-3 gap-2">
              {TIME_SLOTS.map(ts => (
                <button key={ts.key} data-testid={`time-${ts.key}`} onClick={() => toggleTime(ts.key)}
                  className={`py-3 rounded-xl text-center border-2 transition-all ${
                    times.includes(ts.key)
                      ? "border-blue-500 bg-blue-50 dark:bg-blue-900/20"
                      : "border-slate-200 dark:border-slate-700"
                  }`}>
                  <p className={`text-sm font-bold ${times.includes(ts.key) ? "text-blue-600" : "text-slate-600 dark:text-slate-300"}`}>{ts.label}</p>
                  <p className="text-[10px] text-slate-400">{ts.desc}</p>
                </button>
              ))}
            </div>
          </div>

          {/* Duration */}
          <div>
            <p className="text-sm font-bold text-slate-900 dark:text-white mb-2">Lesson Duration</p>
            <div className="grid grid-cols-3 gap-2">
              {[{ d: 60, l: "1 Hour" }, { d: 90, l: "90 Min" }, { d: 120, l: "2 Hours" }].map(({ d, l }) => (
                <button key={d} data-testid={`wl-dur-${d}`} onClick={() => setDuration(d)}
                  className={`py-3 rounded-xl text-center border-2 ${duration === d ? "border-blue-500 bg-blue-50 dark:bg-blue-900/20" : "border-slate-200 dark:border-slate-700"}`}>
                  <p className={`text-sm font-bold ${duration === d ? "text-blue-600" : "text-slate-600 dark:text-slate-300"}`}>{l}</p>
                </button>
              ))}
            </div>
          </div>

          {/* Test Date */}
          <div>
            <p className="text-sm font-bold text-slate-900 dark:text-white mb-2">Practical Test Date <span className="font-normal text-slate-400">(optional)</span></p>
            <input data-testid="wl-test-date" type="date" value={testDate}
              onChange={e => setTestDate(e.target.value)}
              className="w-full border border-slate-200 dark:border-slate-600 rounded-xl px-4 py-3 text-base font-medium bg-white dark:bg-slate-700" />
          </div>

          {/* Urgency */}
          <div>
            <p className="text-sm font-bold text-slate-900 dark:text-white mb-2">How Urgently Do You Need Lessons?</p>
            <div className="grid grid-cols-2 gap-2">
              {[{ key: "asap", label: "ASAP" }, { key: "happy_to_wait", label: "Happy to Wait" }].map(u => (
                <button key={u.key} data-testid={`urgency-${u.key}`} onClick={() => setUrgency(u.key)}
                  className={`py-3 rounded-xl text-center border-2 ${urgency === u.key ? "border-blue-500 bg-blue-50 dark:bg-blue-900/20" : "border-slate-200 dark:border-slate-700"}`}>
                  <p className={`text-sm font-bold ${urgency === u.key ? "text-blue-600" : "text-slate-600 dark:text-slate-300"}`}>{u.label}</p>
                </button>
              ))}
            </div>
          </div>

          {/* Flexibility */}
          <div>
            <p className="text-sm font-bold text-slate-900 dark:text-white mb-2">Flexibility</p>
            <div className="grid grid-cols-3 gap-2">
              {[{ key: "normal", label: "Specific" }, { key: "flexible", label: "Flexible" }, { key: "very_flexible", label: "Very Flexible" }].map(f => (
                <button key={f.key} data-testid={`flex-${f.key}`} onClick={() => setFlexibility(f.key)}
                  className={`py-3 rounded-xl text-center border-2 ${flexibility === f.key ? "border-blue-500 bg-blue-50 dark:bg-blue-900/20" : "border-slate-200 dark:border-slate-700"}`}>
                  <p className={`text-sm font-bold ${flexibility === f.key ? "text-blue-600" : "text-slate-600 dark:text-slate-300"}`}>{f.label}</p>
                </button>
              ))}
            </div>
          </div>

          {/* Package Interest */}
          <label data-testid="pkg-interest-label" className="flex items-center gap-3 cursor-pointer bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 p-4">
            <input data-testid="pkg-interest" type="checkbox" checked={packageInterest}
              onChange={e => setPackageInterest(e.target.checked)} className="sr-only" />
            <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center ${packageInterest ? "bg-blue-600 border-blue-600" : "border-slate-300"}`}>
              {packageInterest && <Check className="w-3.5 h-3.5 text-white" />}
            </div>
            <div>
              <p className="text-sm font-bold text-slate-900 dark:text-white">Interested in a lesson package</p>
              <p className="text-[10px] text-slate-400">Pre-book 4, 8, or 12 lessons for priority access</p>
            </div>
          </label>
        </div>

        {/* Improve Section */}
        {status?.on_waitlist && (
          <div data-testid="improve-priority" className="bg-emerald-50 dark:bg-emerald-900/10 border border-emerald-200 dark:border-emerald-800 rounded-2xl p-4 space-y-2">
            <p className="text-sm font-bold text-emerald-700 dark:text-emerald-400">Improve Your Chances</p>
            <ul className="space-y-1.5 text-xs text-slate-600 dark:text-slate-400">
              {!testDate && <li className="flex items-center gap-2"><Target className="w-3.5 h-3.5 text-emerald-500" />Add your practical test date</li>}
              {times.length < 2 && <li className="flex items-center gap-2"><Clock className="w-3.5 h-3.5 text-emerald-500" />Select more available times</li>}
              {days.length < 3 && <li className="flex items-center gap-2"><Calendar className="w-3.5 h-3.5 text-emerald-500" />Add more preferred days</li>}
              {flexibility === "normal" && <li className="flex items-center gap-2"><TrendingUp className="w-3.5 h-3.5 text-emerald-500" />Increase your flexibility</li>}
              {!packageInterest && <li className="flex items-center gap-2"><Star className="w-3.5 h-3.5 text-emerald-500" />Show interest in a lesson package</li>}
            </ul>
          </div>
        )}

        {/* Submit */}
        <Button data-testid="waitlist-submit" onClick={handleSubmit} disabled={submitting || days.length === 0}
          className={`w-full h-14 rounded-2xl font-extrabold text-base shadow-lg ${
            days.length > 0 ? "bg-blue-600 hover:bg-blue-700 text-white shadow-blue-600/25" : "bg-slate-300 text-slate-500 cursor-not-allowed shadow-none"
          }`}>
          {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> :
            status?.on_waitlist ? "Update Preferences" : "Join Waitlist"}
        </Button>

        {/* Important Note */}
        <p className="text-xs text-slate-400 text-center leading-relaxed">
          Joining the waitlist does not guarantee an immediate slot, but we'll contact you as spaces open.
        </p>

        {/* Leave */}
        {status?.on_waitlist && (
          <button data-testid="leave-waitlist" onClick={leaveWaitlist}
            className="w-full text-center text-xs text-red-400 hover:text-red-500 font-bold py-2">
            Leave Waitlist
          </button>
        )}
      </div>
    </div>
  );
}
